import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";

import { useAuthStore } from "@/stores/auth";
import { isInMiniProgram } from "@/utils/mp-bridge";

/**
 * Routes mirror the five prototype pages per docs/prototypes/ + frontend-h5/CLAUDE.md.
 *
 * Phase 0: empty shells for each route to validate dev-server, lint, type-check.
 * Phase 1: add LoginView + auth guard.
 * Phase 2: wire HomeView / GalleryView / AlbumDetailView / AlbumNewView / ProfileView
 *          to the backend memories endpoints.
 * Phase 3: introduce Shared Albums — see docs/development-plan.md §5.
 *   Note: `/albums/:id` currently maps to a memory detail (per prototype).
 *   Shared Albums will require a path split (e.g. `/albums/shared/:id`); tracked
 *   in the plan to avoid premature churn here.
 */

const routes: RouteRecordRaw[] = [
  {
    path: "/maintenance",
    name: "maintenance",
    component: () => import("@/views/MaintenanceView.vue"),
    meta: { public: true, layout: "blank" },
  },
  {
    path: "/splash",
    name: "splash",
    component: () => import("@/views/SplashView.vue"),
    meta: { public: true, layout: "blank" },
  },
  {
    path: "/login",
    name: "login",
    component: () => import("@/views/LoginView.vue"),
    meta: { public: true, layout: "blank" },
  },
  {
    path: "/register",
    name: "register",
    component: () => import("@/views/LoginView.vue"),
    meta: { public: true, layout: "blank" },
  },
  {
    path: "/auth/wechat/callback",
    name: "wechat-callback",
    component: () => import("@/views/WechatCallbackView.vue"),
    meta: { public: true, layout: "app" },
  },
  {
    path: "/",
    name: "home",
    component: () => import("@/views/HomeView.vue"),
    meta: { layout: "app" },
  },
  {
    path: "/albums",
    name: "albums",
    component: () => import("@/views/GalleryView.vue"),
    meta: { layout: "app" },
  },
  {
    path: "/albums/new",
    name: "albums-new",
    component: () => import("@/views/AlbumNewView.vue"),
    meta: { layout: "app" },
  },
  {
    path: "/albums/:id",
    name: "albums-detail",
    component: () => import("@/views/AlbumDetailView.vue"),
    meta: { layout: "app" },
  },
  {
    path: "/memories/:id",
    name: "memory-detail",
    component: () => import("@/views/MemoryDetailView.vue"),
    meta: { layout: "app" },
  },
  {
    path: "/me",
    name: "me",
    component: () => import("@/views/ProfileView.vue"),
    meta: { layout: "app" },
  },
  {
    path: "/me/edit",
    name: "profile-edit",
    component: () => import("@/views/ProfileEditView.vue"),
    meta: { layout: "blank" },
  },
  {
    path: "/shared",
    name: "shared-list",
    component: () => import("@/views/SharedAlbumListView.vue"),
    meta: { layout: "app" },
  },
  {
    path: "/shared/new",
    name: "shared-new",
    component: () => import("@/views/CreateSharedAlbumView.vue"),
    meta: { layout: "app" },
  },
  {
    path: "/shared/join",
    name: "shared-join",
    component: () => import("@/views/SharedJoinView.vue"),
    // 公开路由：未登录用户访问邀请链接不应被 redirect 丢弃 token。
    // 登录态在组件内自行检查：未登录显示"先登录/注册"按钮；登录后自动 join。
    meta: { public: true, layout: "app" },
  },
  {
    path: "/shared/:id",
    name: "shared-detail",
    component: () => import("@/views/SharedAlbumDetailView.vue"),
    meta: { layout: "app" },
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: { name: "home" },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 };
  },
});

/**
 * 是否启用维护模式
 */
const isMaintenanceMode = (): boolean => {
  return import.meta.env.VITE_MAINTENANCE_MODE === "true";
};

/**
 * Route guard.
 *   - 维护模式优先级最高：除维护页本身外，所有路由重定向到 /maintenance
 *     但小程序 web-view 嵌入场景豁免（通过 URL 参数或 wx.miniProgram 检测）
 *   - On first navigation, wait for auth bootstrap (refresh-token → access-token
 *     exchange) so that F5 refresh doesn't bounce to /splash while the token
 *     is still being restored from localStorage.
 *   - Unauthenticated user hits a protected route → /splash
 *   - Authenticated user hits /splash, /login, or /register → /
 */
router.beforeEach(async (to) => {
  // 维护模式拦截（小程序 web-view 嵌入场景豁免）
  if (isMaintenanceMode() && to.name !== "maintenance") {
    if (!isInMiniProgram()) {
      return { name: "maintenance" };
    }
  }

  const auth = useAuthStore();
  await auth.bootstrap();
  const isAuthed = auth.isAuthenticated;
  const isPublic = to.meta.public === true;

  // Unauthenticated user hitting a protected route → splash (or login).
  if (!isAuthed && !isPublic) {
    return { name: "splash" };
  }
  // Authenticated user hitting splash/login/register → home.
  if (isAuthed && (to.name === "splash" || to.name === "login" || to.name === "register")) {
    return { name: "home" };
  }
  return true;
});
